import {LegacyWBIcon} from '@wandb/weave/common/components/elements/LegacyWBIcon';
import SliderInput from '@wandb/weave/common/components/elements/SliderInput';
import {PopupDropdown} from '@wandb/weave/common/components/PopupDropdown';
import classNames from 'classnames';
import React, {useCallback} from 'react';
import {useParams} from 'react-router';
import {Button, Dropdown, Popup} from 'semantic-ui-react';

import {smoothingChanged} from './../../services/analytics';
import {
  smoothingOptions,
  smoothingOptionsMeta,
  SmoothingTypeValues,
} from './SmoothingConfig';

interface SmoothingInputProps {
  smoothingParam?: number;
  smoothingType?: SmoothingTypeValues;
  smoothingTypeDropdown?: boolean;
  smoothingTypePopupDropdown?: boolean;
  dropdownDirection?: 'left' | 'right';
  onChange(smoothingParam: number, smoothingType: SmoothingTypeValues): void;
}

function useTrackSmoothingChanged() {
  const {entityName, projectName} = useParams<{
    entityName: string;
    projectName: string;
  }>();
  return useCallback(
    (smoothingType: SmoothingTypeValues) => {
      smoothingChanged({
        entityName,
        projectName,
        smoothingType,
      });
    },
    [entityName, projectName]
  );
}

// get the default selected option from the config
const defaultSmoothingType = 'exponential';

// read the default values for the UI inputs from the config
const defaultsFromSmoothingType = (smoothingType: SmoothingTypeValues) =>
  smoothingOptionsMeta[smoothingType].defaults;

export function useSmoothingOptions(
  setSmoothingType: (s: SmoothingTypeValues) => void,
  smoothingType: SmoothingTypeValues
) {
  return React.useMemo(() => {
    return smoothingOptions.map(option => ({
      ...option,
      active:
        smoothingType === option.value ||
        (smoothingType === null && option.value === defaultSmoothingType),
      onClick: () => setSmoothingType(option.value),
    }));
  }, [setSmoothingType, smoothingType]);
}

const SmoothingInput = (props: SmoothingInputProps) => {
  const {smoothingParam} = props;
  const smoothingType = props.smoothingType ?? defaultSmoothingType;
  const smoothingDefaults = defaultsFromSmoothingType(
    smoothingType ?? defaultSmoothingType
  );
  const trackSmoothingChanged = useTrackSmoothingChanged();

  const setSmoothingType = (newSmoothingType: SmoothingTypeValues) => {
    const newSmoothingDefaults = defaultsFromSmoothingType(newSmoothingType);
    props.onChange(newSmoothingDefaults.default, newSmoothingType);
    trackSmoothingChanged(newSmoothingType);
  };

  const options = useSmoothingOptions(
    setSmoothingType,
    smoothingType ?? 'none'
  );

  const renderSmoothingTypeButton = (
    buttonSmoothingType: SmoothingTypeValues,
    helpText: string
  ) => {
    return (
      <Popup
        inverted
        size="mini"
        trigger={
          <Button
            size="small"
            icon
            className={classNames('wb-icon-button', 'only-icon', {
              'action-button--active':
                props.smoothingType === buttonSmoothingType,
            })}
            onClick={() => {
              setSmoothingType(buttonSmoothingType);
            }}>
            <LegacyWBIcon name={smoothingOptionsMeta[smoothingType].iconName} />
          </Button>
        }
        content={helpText}
      />
    );
  };

  return (
    <>
      <SliderInput
        min={smoothingDefaults.min}
        max={smoothingDefaults.max}
        step={smoothingDefaults.step}
        value={smoothingParam ?? smoothingDefaults.default}
        disabled={smoothingType === 'none'}
        debounceTime={100}
        hasInput
        allowGreaterThanMax
        onChange={value =>
          props.onChange(value, smoothingType ?? defaultSmoothingType)
        }
      />
      {props.smoothingTypePopupDropdown && (
        <PopupDropdown
          trigger={
            <div className="smoothing-type-trigger">
              <Button icon>
                <LegacyWBIcon
                  name={smoothingOptionsMeta[smoothingType].iconName}
                />
              </Button>
            </div>
          }
          options={options}
        />
      )}
      {props.smoothingTypeDropdown && (
        <Dropdown
          value={smoothingType}
          direction={props.dropdownDirection ?? 'right'}
          className="smoothing-dropdown"
          trigger={
            <div className="smoothing-type-trigger">
              <Button icon>
                <LegacyWBIcon
                  name={smoothingOptionsMeta[smoothingType].iconName}
                />
              </Button>
            </div>
          }
          options={options}
        />
      )}
      {!props.smoothingTypeDropdown && !props.smoothingTypePopupDropdown && (
        <Button.Group size="tiny" className="smoothing-type-options">
          {renderSmoothingTypeButton('none', 'Turn off smoothing')}
          {renderSmoothingTypeButton(
            'exponentialTimeWeighted',
            'Exponential Moving Average'
          )}
          {renderSmoothingTypeButton('gaussian', 'Gaussian smoothing')}
          {renderSmoothingTypeButton('average', 'Running average smoothing')}
          {renderSmoothingTypeButton(
            'exponential',
            "Exponential Moving Average (Tensorboard's algorithm - Deprecated)"
          )}
        </Button.Group>
      )}
    </>
  );
};

export default SmoothingInput;
